import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import { useSoundStatus } from '../../context/SoundStatusContext';

import Layout from '../UI/introGameLayout/layout';

import { 
  scenarioLevelsQuery,
  scoresStatsQuery,
  gamesQuery,
  getGamesSessions
} from './api';

import LevelCompletedSound from './sounds/level-summary.mp3';

import StarEmpty from './images/icon-star-empty.svg';
import StarFilled from './images/icon-star-filled.svg';

import { gameQuestionsLimit } from './data';

import './styles.css';

export default function NormalBalancePoints() {
  const params = useParams();
  const levelNumber = Number(params.levelNumber);
  
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { getCurrentNormalGameId, getUser } = useAuth();

  const user = getUser();

  const gameId = getCurrentNormalGameId();

  const [scenarioId, setScenarioId] = useState(0);

  const { data: games } = useQuery(gamesQuery()) || {};

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('Normal Balance') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);

  const { soundStatus } = useSoundStatus();

  const levelCompletedEffect = useRef(new Audio(LevelCompletedSound));

  useEffect(() => {
    if (soundStatus === 'enabled') {
      levelCompletedEffect.current.play().catch((e) => console.error('Error playing level completed sound:', e));
    } else {
      if (levelCompletedEffect.current) {
        levelCompletedEffect.current.pause();
      }
    }
  }, [soundStatus]);

  const [rank, setRank] = useState('');
  const [questionsQuantity, setQuestionsQuantity] = useState(0);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);

  const { data: gamesSessions } = useQuery({
    queryKey: ['gamesSessionsSummary'],
    queryFn: getGamesSessions,
    refetchOnWindowFocus: false,
  });

  const [currentGameSessionAttempt, setCurrentGameSessionAttempt] = useState(0);

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSession = gamesSessions.find(session => session.user === user?.id && session.game === gameId && !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionAttempt(currentGameSession?.attempt);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionAttempt(completedGameSession?.attempt);
      }
    }
  }, [gamesSessions, gameId, user]);

  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({scenarioId})) || {};
  const { data: scoresStats } = useQuery(scoresStatsQuery({ gameId, attempt: currentGameSessionAttempt }));

  // For tournament games, skip this page and go to the next level (or
  // summary if this was the last level).
  const currentGame = games?.find(game => game.id === gameId);
  const isTournamentGame = currentGame?.tournament_ids?.length > 0;
  useEffect(() => {
    if (!isTournamentGame || !scenarioLevels?.length) return;
    if (scenarioLevels.length === levelNumber) {
      navigate('/normal/summary', { replace: true });
    } else {
      navigate(`/normal/level/${levelNumber + 1}/`, { replace: true });
    }
  }, [isTournamentGame, scenarioLevels, levelNumber, navigate]);

  const [levelsCount, setLevelsCount] = useState(0);

  useEffect(() => {
    if(scoresStats && scenarioLevels?.length) {
      setLevelsCount(scenarioLevels.length);
      const scenarioLevel = scenarioLevels.find(scenarioLevel => scenarioLevel.level === levelNumber);
      
      const totalQuestions = Math.min(gameQuestionsLimit, scenarioLevel.normal_questions.length)
      
      const levelScoresStats = scoresStats.levels?.find(scoresStat => scoresStat.level === levelNumber);

      // Rank is computed server-side and returned per level — no need to
      // re-derive the Master/Warrior/Newbie/Noob tier on the frontend.
      setRank(levelScoresStats?.rank);
      setScore(levelScoresStats?.user_points);
      setBestScore(levelScoresStats?.highest_points);
      
      setQuestionsQuantity(totalQuestions);
    }
  }, [scoresStats, scenarioLevels, levelNumber]);

  const firstStarValue = questionsQuantity * 50;
  const secondStarValue = questionsQuantity * 70;
  const thirdStarValue = questionsQuantity * 90;

  const firstStarImage = score >= firstStarValue ? StarFilled : StarEmpty;
  const secondStarImage = score >= secondStarValue ? StarFilled : StarEmpty;
  const thirdStarImage = score >= thirdStarValue ? StarFilled : StarEmpty;

  const handleNextButtonClick = async () => {
    if (levelCompletedEffect.current) {
      levelCompletedEffect.current.pause();
    }

    if(scenarioLevels?.length === levelNumber) {
      navigate(`/normal/badge`);
    } else {
      let redirectUrl = `/normal/level/${levelNumber + 1}/`;

      const hasCompletedLevelTypeBefore = async (levelName, sessionsForGameType) => {
        const scenarioIds = [...new Set(
          sessionsForGameType
            .map(session => {
              const sessionGame = games.find(game => game.id === session.game);
              return sessionGame?.scenario;
            })
            .filter(Boolean)
        )];
        console.log('scenarioIds', scenarioIds);
  
        if (scenarioIds.length === 0) return false;
  
        const allScenarioLevels = [];
    
        for (const scId of scenarioIds) {
          if (scId === scenarioId) {
            allScenarioLevels.push(...scenarioLevels.map(level => ({ ...level, scenarioId: scId })));
            continue;
          }
  
          console.log('scenarioId', scenarioId);
          const levels = await queryClient.fetchQuery({ ...scenarioLevelsQuery({ scenarioId: scId }), staleTime: Infinity }).catch(error => {
            console.error(`Error fetching scenario levels ${scId}:`, error);
            return null;
          });
  
          if (levels) {
            allScenarioLevels.push(...levels.map(level => ({ ...level, scenarioId: scId })));
          }
        }
  
        return sessionsForGameType.some(session => {
          const sessionGame = games.find(game => game.id === session.game);
          if (!sessionGame || !sessionGame.scenario) return false;
  
          const matchingLevels = allScenarioLevels.filter(
            scenarioLevel => scenarioLevel.scenarioId === sessionGame.scenario && scenarioLevel.name === levelName
          );
  
          return matchingLevels.some(level => {
            const levelKey = `level${level.level}`;
            return session[levelKey] === 'completed';
          });
        });
      };
      
      const sessionsForGameType = gamesSessions?.filter(
        session =>
          session.user === user?.id &&
          games.find(game => game.id === session.game && game.game_type === 'Normal Balance')
      );
      const nextScenarioLevel = scenarioLevels?.find(scenarioLevel => scenarioLevel.level === levelNumber + 1);

      const hasNeverCompletedThisType = !(await hasCompletedLevelTypeBefore(
        nextScenarioLevel?.name,
        sessionsForGameType
      ));

      if (hasNeverCompletedThisType) {
        redirectUrl = '/acc/transition';
      }

      navigate(redirectUrl);
    }
  }

  return (
    <Layout isHeaderVisible={true}>
      <div className="page-content level-summary-page-content">
        <div className="summary-block-container">
          <h1 className="summary-page-title">Level {levelNumber} complete</h1>
          <p className="summary-page-description">Points are determined by both speed and accuracy, and maximum points vary from one level to the next.</p>
          <div className="summary-block">
            <div className="summary-block-item">
              <h3 className="summary-level">Level {levelNumber}</h3>
              {
                rank &&
                  <>
                    <h2 className="summary-rank">{rank}</h2>
                    <div className="summary-stars-rating">
                      <div className="summary-star-block">
                        <img src={firstStarImage} alt="First star" />
                        <span className="summary-star-value">{firstStarValue}</span>
                      </div>
                      <div className="summary-star-block">
                      <img src={secondStarImage} alt="First star" />
                        <span className="summary-star-value">{secondStarValue}</span>
                      </div>
                      <div className="summary-star-block">
                      <img src={thirdStarImage} alt="First star" />
                        <span className="summary-star-value">{thirdStarValue}</span>
                      </div>
                    </div>
                  </>
              }
            </div>
            <div className="summary-block-item">
              <h3 className="summary-score">Score: {score}</h3>
              <h4 className="summary-best-score">Best: {bestScore}</h4>
            </div>
          </div>
        </div>
        <button className="button-red summary-next-button" onClick={handleNextButtonClick}>Next</button>
      </div>
    </Layout>
  );
}